/**
 * LaunchActivityCard — dashboard widget rendering a 30-day rollup of
 * program launches for the clinic + per-vendor health.
 *
 * Rendered from PortalIntelligence renderWidget("launch_activity").
 * Fetches its own data (independent from /clinic/intelligence) so the
 * dashboard doesn't need a schema change to add it.
 *
 * Iter 95: added
 *   - clickable recent rows opening a per-launch Timeline modal that
 *     walks each vendor through their status_history
 *   - "Overdue" section listing dispatches whose complete_by has
 *     already passed and are not yet completed/cancelled, so the
 *     clinic can intervene
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CheckCircle2, Rocket,
  Clock, X, Mail, Play, XCircle, Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending:      { bg: "bg-amber-50",   ring: "ring-amber-200",   text: "text-amber-800",   dot: "bg-amber-500",   icon: Clock },
  accepted:     { bg: "bg-blue-50",    ring: "ring-blue-200",    text: "text-blue-800",    dot: "bg-blue-500",    icon: CheckCircle2 },
  in_progress:  { bg: "bg-violet-50",  ring: "ring-violet-200",  text: "text-violet-800",  dot: "bg-violet-500",  icon: Play },
  completed:    { bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500", icon: CheckCircle2 },
  cancelled:    { bg: "bg-slate-50",   ring: "ring-slate-200",   text: "text-slate-600",   dot: "bg-slate-400",   icon: XCircle },
  email_failed: { bg: "bg-rose-50",    ring: "ring-rose-200",    text: "text-rose-800",    dot: "bg-rose-500",    icon: Mail },
};

const STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  email_failed: "Email failed",
};

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.max(0, Math.round((now - then) / 60000));
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
  return `${Math.round(diffMin / (60 * 24))}d ago`;
}

function StatBlock({ label, value, tone }) {
  const toneClass =
    tone === "success" ? "text-emerald-600"
    : tone === "warn"  ? "text-amber-600"
    : tone === "danger" ? "text-rose-600"
    : "text-slate-900";
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`mt-1 font-display font-extrabold text-2xl leading-none ${toneClass}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 ${s.bg} ${s.ring} ${s.text}`}>
      <span className={`w-1 h-1 rounded-full ${s.dot}`} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

/**
 * Per-launch timeline modal — shows each dispatch's status_history
 * as a vertical timeline so clinics can trace when a vendor accepted,
 * started, and completed the service.
 */
function TimelineModal({ launchId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/clinic/program-launch/history/${launchId}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [launchId]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="launch-timeline-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-1">
              Launch timeline
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900 leading-tight truncate">
              {detail?.program_name || "Loading…"}
            </h3>
            {detail && (
              <p className="mt-1 text-xs text-slate-500">
                {detail.consumer?.first_name} {detail.consumer?.last_name}
                {detail.start_date && <> · started {detail.start_date}</>}
                {detail.complete_by && <> · due {detail.complete_by}</>}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="launch-timeline-close"
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Loading timeline…
            </div>
          ) : !detail || !(detail.dispatches || []).length ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              No vendor dispatches on this launch.
            </div>
          ) : (
            <div className="space-y-5">
              {(detail.dispatches || []).map((d) => {
                const history = d.status_history || [];
                const S = STATUS_STYLE[d.status] || STATUS_STYLE.pending;
                return (
                  <div
                    key={d.id || d.vendor_id}
                    data-testid={`timeline-vendor-${d.vendor_id}`}
                    className="rounded-xl border border-slate-200 bg-slate-50/40 p-4"
                  >
                    <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                          {d.category || "Service"}
                        </div>
                        <div className="font-semibold text-slate-900 text-sm">
                          {d.vendor_name || "Vendor"}
                        </div>
                        {d.contact_email && (
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                            {d.contact_email}
                          </div>
                        )}
                      </div>
                      <StatusPill status={d.status || "pending"} />
                    </div>

                    {history.length === 0 ? (
                      <div className="text-[11px] text-slate-400 italic">No status changes recorded yet.</div>
                    ) : (
                      <div className="relative pl-4 border-l-2 border-slate-200">
                        {history.map((h, i) => {
                          const HS = STATUS_STYLE[h.status] || STATUS_STYLE.pending;
                          return (
                            <div
                              key={i}
                              className="relative pb-3 last:pb-0"
                              data-testid={`timeline-event-${d.vendor_id}-${i}`}
                            >
                              <span className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full ring-2 ring-white ${HS.dot}`} />
                              <div className="flex items-start justify-between gap-2 flex-wrap">
                                <div className="text-xs">
                                  <div className="font-semibold text-slate-900">
                                    {STATUS_LABEL[h.status] || h.status}
                                  </div>
                                  {h.note && <div className="text-slate-500 italic">{h.note}</div>}
                                  <div className="text-[10.5px] text-slate-400">
                                    {h.actor || "system"}
                                  </div>
                                </div>
                                <div className="text-[10.5px] text-slate-400 font-mono flex-shrink-0">
                                  {h.at ? new Date(h.at).toLocaleString() : ""}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {d.reminder_sent_at && (
                      <div className="mt-3 pt-2 border-t border-slate-200 text-[10.5px] text-slate-500 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        Reminder emailed {new Date(d.reminder_sent_at).toLocaleString()}
                      </div>
                    )}
                    {S && d.error && (
                      <div className="mt-3 pt-2 border-t border-slate-200 text-[10.5px] text-rose-700">
                        Error: {d.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LaunchActivityCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLaunchId, setOpenLaunchId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/clinic/program-launch/activity-summary", { params: { days: 30 } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="launch-activity-loading">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Launch activity</div>
        <div className="mt-3 h-16 rounded-lg bg-slate-100 animate-pulse" />
      </div>
    );
  }
  if (!data) return null;

  const { stats = {}, recent = [], vendor_health = [], at_risk_count = 0, overdue = [], overdue_count = 0, window_days } = data;
  const isEmpty = (stats.launches || 0) === 0;
  const successPct = stats.success_rate != null ? Math.round(stats.success_rate * 100) : null;
  const successTone = successPct == null ? "" : successPct >= 90 ? "success" : successPct >= 70 ? "warn" : "danger";
  const atRiskVendors = vendor_health.filter((v) => v.at_risk).slice(0, 3);

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="launch-activity-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
              Launch activity · last {window_days} days
            </div>
          </div>
          <Link
            to="/portal/launch"
            data-testid="launch-activity-cta"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:underline"
          >
            Launch a program <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {isEmpty ? (
          <div className="rounded-xl bg-slate-50 border border-slate-100 p-5 text-center" data-testid="launch-activity-empty">
            <Rocket className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <div className="text-sm font-semibold text-slate-700">No launches yet this month</div>
            <div className="text-xs text-slate-500 mt-1">Launch your first program to see activity here.</div>
            <Link
              to="/portal/launch"
              className="mt-3 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 py-2 text-xs font-semibold"
            >
              <Rocket className="w-3 h-3" /> Launch program
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatBlock label="Launches" value={stats.launches} />
              <StatBlock label="Consumers" value={stats.consumers} />
              <StatBlock
                label="Dispatch success"
                value={successPct == null ? "—" : `${successPct}%`}
                tone={successTone}
              />
              <StatBlock
                label="Overdue"
                value={overdue_count}
                tone={overdue_count > 0 ? "danger" : "success"}
              />
            </div>

            {overdue_count > 0 && (
              <div className="mt-4 rounded-xl bg-rose-50 border border-rose-200 p-3" data-testid="launch-activity-overdue">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-rose-700 mb-1.5">
                  <AlertTriangle className="w-3 h-3" /> Overdue dispatches
                </div>
                <div className="space-y-1.5">
                  {overdue.slice(0, 4).map((o) => (
                    <button
                      type="button"
                      key={`${o.launch_id}:${o.vendor_id}`}
                      onClick={() => setOpenLaunchId(o.launch_id)}
                      data-testid={`launch-activity-overdue-${o.vendor_id}`}
                      className="w-full text-left flex items-center justify-between gap-2 text-xs text-slate-800 hover:bg-white/50 rounded px-1 py-0.5 transition-colors"
                    >
                      <span className="truncate min-w-0 flex-1">
                        <span className="font-semibold">{o.vendor_name}</span>
                        <span className="text-slate-500"> · {o.consumer_display || "—"}</span>
                      </span>
                      <span className="font-mono text-[11px] text-rose-700 flex-shrink-0">
                        {o.days_overdue}d overdue
                      </span>
                    </button>
                  ))}
                  {overdue.length > 4 && (
                    <div className="text-[11px] text-rose-600 pt-1">
                      +{overdue.length - 4} more · click a row to open timeline
                    </div>
                  )}
                </div>
              </div>
            )}

            {atRiskVendors.length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="launch-activity-at-risk">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1.5">
                  <AlertTriangle className="w-3 h-3" /> Vendor health flags
                </div>
                <div className="space-y-1">
                  {atRiskVendors.map((v) => (
                    <div
                      key={v.vendor_id || v.vendor_name}
                      data-testid={`launch-activity-at-risk-${v.vendor_id}`}
                      className="text-xs text-slate-800 flex items-center justify-between"
                    >
                      <span className="font-semibold truncate">{v.vendor_name}</span>
                      <span className="font-mono text-[11px] text-amber-800 flex-shrink-0 ml-2">
                        {Math.round((v.success_rate || 0) * 100)}% · {v.sent}/{v.dispatches}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Recent · click a row for timeline
              </div>
              <div className="space-y-1.5" data-testid="launch-activity-recent">
                {recent.slice(0, 4).map((r) => {
                  const vsc = r.vendor_status_counts || {};
                  const active = (vsc.pending || 0) + (vsc.accepted || 0) + (vsc.in_progress || 0);
                  const done = vsc.completed || 0;
                  const total = active + done + (vsc.cancelled || 0) + (vsc.email_failed || 0);
                  const failed = (r.dispatch_summary?.failed || 0) > 0 || (vsc.email_failed || 0) > 0;
                  return (
                    <button
                      type="button"
                      key={r.id}
                      onClick={() => setOpenLaunchId(r.id)}
                      data-testid={`launch-activity-recent-${r.id}`}
                      className="w-full text-left flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-100 hover:bg-slate-100 hover:border-slate-200 transition-colors px-2 py-1.5 text-xs"
                    >
                      {failed ? (
                        <AlertTriangle className="w-3 h-3 text-amber-600 flex-shrink-0" />
                      ) : done === total ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                      ) : (
                        <Clock className="w-3 h-3 text-blue-600 flex-shrink-0" />
                      )}
                      <span className="font-semibold text-slate-900 truncate min-w-0 flex-1">
                        {r.consumer_display || "—"} · {r.program_name}
                      </span>
                      <span className="text-slate-500 flex-shrink-0 font-mono">
                        {done}/{total} done
                      </span>
                      <span className="text-slate-400 flex-shrink-0">{relTime(r.created_at)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {openLaunchId && (
        <TimelineModal launchId={openLaunchId} onClose={() => setOpenLaunchId(null)} />
      )}
    </>
  );
}
